import { UI_ICONS } from "@/lib/ui-icons";
import { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import { Iconify } from "@/lib/iconify";

interface ActionConfirmOpts {
  title: string;
  sub?: string;
  warning?: string;
  confirmLabel?: string;
  onConfirm?: () => void;
}

interface State extends ActionConfirmOpts {
  open: boolean;
}

const CLOSED: State = { open: false, title: "" };

export function ActionConfirmProvider() {
  const [state, setState] = useState<State>(CLOSED);

  const handleOpen = useCallback((e: Event) => {
    const opts = (e as CustomEvent<ActionConfirmOpts>).detail;
    setState({ ...opts, open: true });
  }, []);

  useEffect(() => {
    window.addEventListener("blyn:action-confirm", handleOpen);
    return () => window.removeEventListener("blyn:action-confirm", handleOpen);
  }, [handleOpen]);

  const handleCancel = () => setState(CLOSED);

  const handleConfirm = () => {
    const cb = state.onConfirm;
    setState(CLOSED);
    cb?.();
  };

  return (
    <Dialog
      open={state.open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", border: "1px solid", borderColor: "divider", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
          color: "warning.main",
          display: "grid", placeItems: "center",
        }}>
          <Iconify icon={UI_ICONS.dangerTriangle} width={18} />
        </Box>
        <Box sx={{ flex: 1, pt: 0.25 }}>
          <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
            {state.title}
          </Typography>
          {state.sub && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {state.sub}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleCancel} sx={{ mt: -0.5, mr: -0.5, color: "text.secondary" }}>
          <Iconify icon={UI_ICONS.closeLine} width={18} />
        </IconButton>
      </DialogTitle>

      {state.warning && (
        <DialogContent sx={{ px: 2.5, pt: 0.5, pb: 1 }}>
          <Box sx={{
            display: "flex", alignItems: "flex-start", gap: 1,
            p: "10px 12px", borderRadius: "10px",
            border: "1px solid", borderColor: (t) => alpha(t.palette.warning.main, 0.35),
            bgcolor: (t) => alpha(t.palette.warning.main, 0.07),
          }}>
            <Iconify icon={UI_ICONS.dangerTriangle} width={14} color="warning.main" sx={{ mt: 0.1, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: "warning.main", lineHeight: 1.55 }}>
              {state.warning}
            </Typography>
          </Box>
        </DialogContent>
      )}

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: state.warning ? 1 : 1.5, gap: 1 }}>
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          startIcon={<Iconify icon={UI_ICONS.checkRead} width={15} />}
        >
          {state.confirmLabel || "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
